import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money, itemImage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OutfitCard } from "@/components/OutfitCard";
import { PostCard, MarketCard } from "@/components/Social";
import { BuildLookDialog } from "@/components/Lookbook";
import { WorthChart } from "@/components/WorthChart";
import { DailyHero } from "@/components/DailyHero";
import { WeeklyRecap, useBadgeUnlocks } from "@/components/Badges";
import { weatherIcon } from "@/components/WeatherWidget";
import { Button } from "@/components/ui/button";
import { ReviewBanner } from "@/pages/Review";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, CalendarDays, MapPin, ArrowRight, ArrowUp, Flame, Inbox, Loader2, Shuffle, BookHeart, Tag, Users } from "lucide-react";
import { toast } from "sonner";

const timeOfDay = () => { const h = new Date().getHours(); return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"; };

const SUGGESTIONS = ["Style me for a rooftop dinner", "Something warm for a rainy commute", "Date night, sharp but not stiff", "Casual Sunday, I'm walking a lot"];

/* ---------- AskThreadList: plain-English prompt -> outfit from your closet ---------- */
const AskPanel = ({ outfit, setOutfit, itemCount }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState(null);
  const [build, setBuild] = useState(false);
  const inputRef = useRef(null);

  const ask = async (prompt, lockedIds = []) => {
    const text = (prompt ?? q).trim();
    setBusy(true); setAsked(text || null);
    try {
      const res = await api.outfitToday({ time_of_day: timeOfDay(), occasion: text || undefined, locked_item_ids: lockedIds.length ? lockedIds : undefined });
      setOutfit(res); setQ("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't style that right now"); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="ask-panel">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
        <span className="h-8 w-8 rounded-full bg-foreground text-background inline-flex items-center justify-center text-[10px] font-extrabold">AT</span>
        <div className="min-w-0"><p className="text-sm font-semibold leading-none">AskThreadList</p><p className="text-[11px] text-muted-foreground mt-1 truncate">Knows every piece in your closet. Weather-aware.</p></div>
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={() => setBuild(true)} className="rounded-full h-8" data-testid="dashboard-build-look-button"><Plus className="h-4 w-4 mr-1" /> Build</Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/planner")} className="rounded-full h-8" data-testid="dashboard-plan-week-button"><CalendarDays className="h-4 w-4 mr-1" /> Week</Button>
        </div>
      </div>

      <div className="p-5">
        {itemCount === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center">
            <p className="font-display text-xl">Your closet is empty</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">Add your pieces and AskThreadList will dress your twin for today's weather.</p>
            <Button onClick={() => navigate("/add")} className="mt-4 rounded-full" data-testid="dashboard-add-item-button"><Plus className="h-4 w-4 mr-1.5" /> Add pieces</Button>
          </div>
        ) : busy ? (
          <div className="space-y-3" data-testid="ask-loading">
            {asked && <div className="rounded-2xl rounded-tr-md bg-foreground text-background px-4 py-2.5 text-sm w-fit ml-auto">{asked}</div>}
            <div className="rounded-2xl rounded-tl-md bg-gray-50 px-4 py-2.5 text-sm w-fit inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Going through your closet…</div>
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-4 pt-2">
              <Skeleton className="aspect-[3/4] rounded-xl" />
              <div className="grid grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl" />)}</div>
            </div>
          </div>
        ) : outfit ? (
          <div className="space-y-4">
            {(asked || outfit.occasion) && <div className="rounded-2xl rounded-tr-md bg-foreground text-background px-4 py-2.5 text-sm w-fit ml-auto" data-testid="ask-prompt-echo">{asked || outfit.occasion}</div>}
            <OutfitCard outfit={outfit} onChange={setOutfit} onShuffle={(locked) => ask(outfit.occasion || "", locked)} shuffling={busy} />
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground">Try one of these, or ask anything:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="rounded-full border border-border px-3.5 h-9 text-sm hover:bg-secondary transition-colors press" data-testid="ask-suggestion">{s}</button>)}
            </div>
          </div>
        )}

        {itemCount > 0 && (
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-5 flex items-center gap-2">
            <Input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={outfit ? "Change something? e.g. “make it warmer”" : "Ask anything… “what do I wear to a gallery opening?”"} className="h-12 rounded-full px-5" disabled={busy} data-testid="ask-input" />
            <Button type="submit" disabled={busy} className="rounded-full h-12 w-12 p-0 shrink-0" aria-label="Ask" data-testid="dashboard-generate-outfit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-5 w-5" />}</Button>
            {outfit && !busy && <Button type="button" variant="outline" onClick={() => ask("")} className="rounded-full h-12 shrink-0 hidden sm:inline-flex" data-testid="dashboard-shuffle-button"><Shuffle className="h-4 w-4 mr-1.5" /> Shuffle</Button>}
          </form>
        )}
      </div>
      <BuildLookDialog open={build} onOpenChange={setBuild} onCreated={(o) => { setOutfit(o); setAsked(null); }} />
    </section>
  );
};

/* ---------- Fit streak ---------- */
const StreakCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.streak().then(setS).catch(() => setS({ current: 0, longest: 0, days: [] })); }, []);
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="streak-card">
      <div className="flex items-center justify-between">
        <p className="eyebrow"><Flame className={`h-3.5 w-3.5 ${s?.current ? "text-wait" : ""}`} /> Fit streak</p>
        {s && <p className="text-[11px] text-muted-foreground">best {s.longest}</p>}
      </div>
      {s ? <p className="font-display text-[32px] leading-none mt-2" data-testid="streak-current">{s.current} <span className="text-base font-medium text-muted-foreground">day{s.current === 1 ? "" : "s"}</span></p> : <Skeleton className="h-8 w-24 mt-2" />}
      <div className="mt-3 grid gap-1" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }} aria-hidden="true">
        {(s?.days || Array.from({ length: 14 }, () => ({}))).map((d, i) => <span key={d.date || i} className={`h-2 rounded-full ${d.active ? "bg-foreground" : "bg-gray-100"}`} title={d.date} />)}
      </div>
      <p className="text-xs text-muted-foreground mt-3">{s?.active_today ? "You're on for today." : "Log what you wore or post a fit to keep it alive."}</p>
    </section>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  useBadgeUnlocks();
  const [weather, setWeather] = useState(null);
  const [stats, setStats] = useState(null);
  const [outfit, setOutfit] = useState(null);
  const [items, setItems] = useState(null);
  const [feed, setFeed] = useState(null);
  const [market, setMarket] = useState(null);
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    api.getWeather().then(setWeather).catch(() => {});
    api.getStats().then(setStats).catch(() => {});
    api.listItems({}).then((l) => setItems(l.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 8))).catch(() => setItems([]));
    api.feed("all").then((p) => setFeed(p.slice(0, 3))).catch(() => setFeed([]));
    api.market({ fit: true }).then((m) => setMarket(m.items.slice(0, 5))).catch(() => setMarket([]));
    api.offersPending().then(setOffers).catch(() => {});
    api.listOutfits({ limit: 5 }).then((list) => {
      const today = new Date().toISOString().slice(0, 10);
      const t = list.find((o) => o.date === today && o.source !== "manual" && (o.items || []).length);
      if (t) setOutfit(t);
    }).catch(() => {});
  }, []);

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const c = weather?.current;
  const first = (user?.display_name || user?.handle || "").split(" ")[0];
  const count = stats?.total_items ?? null;

  return (
    <div data-testid="home-page" className="space-y-10">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <p className="eyebrow">{today}</p>
          <h1 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-2">Good {timeOfDay()}{first ? `, ${first}` : ""}.</h1>
        </div>
        {c ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-border px-3.5 h-10 text-sm" data-testid="dashboard-weather-widget">
            {weatherIcon(c.code, "h-4 w-4")}
            <span className="font-mono-num font-semibold">{Math.round(c.temp)}{c.unit}</span>
            <span className="text-muted-foreground">{c.description}</span>
            <span className="text-muted-foreground inline-flex items-center gap-1 border-l border-border pl-2"><MapPin className="h-3 w-3" /><span data-testid="weather-location">{weather.location_label}</span></span>
          </span>
        ) : <Skeleton className="h-10 w-64 rounded-full" />}
      </div>

      {/* main grid */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start">
        <div className="space-y-5 min-w-0">
          {count != null && <DailyHero itemCount={count} />}
          <AskPanel outfit={outfit} setOutfit={setOutfit} itemCount={count ?? 1} />
        </div>
        <div className="space-y-5">
          <WorthChart compact />
          <StreakCard />
          <WeeklyRecap />
          {offers?.pending > 0 && (
            <Link to="/market?tab=offers" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 lift" data-testid="home-offers-waiting">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-50"><Inbox className="h-5 w-5 text-wait" /></span>
              <div className="min-w-0"><p className="text-sm font-semibold">{offers.pending} offer{offers.pending === 1 ? "" : "s"} waiting on you</p><p className="text-xs text-muted-foreground">{offers.unread ? `${offers.unread} unread message${offers.unread === 1 ? "" : "s"} · ` : ""}Accept, counter or chat.</p></div>
              <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
            </Link>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            {[[Plus, "Add pieces", "/add", "home-shortcut-add"], [BookHeart, "Lookbook", "/looks", "home-shortcut-looks"], [Tag, "Sell & trade", `/u/${user?.handle}?tab=manage`, "home-shortcut-sell"], [Users, "People", "/social?tab=people", "home-shortcut-people"]].map(([Icon, t, to, tid]) => (
              <Link key={t} to={to} className="flex items-center gap-2.5 rounded-2xl border border-border bg-card px-3.5 h-12 text-sm font-medium hover:bg-secondary transition-colors" data-testid={tid}><Icon className="h-4 w-4" /> {t}</Link>
            ))}
          </div>
        </div>
      </div>

      <ReviewBanner stats={stats} />

      {/* closet preview */}
      <section>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div><p className="eyebrow">My closet</p><h2 className="font-display text-[26px] mt-1">{count != null ? `${count} piece${count === 1 ? "" : "s"}` : "…"}{stats ? <span className="text-muted-foreground font-medium text-base ml-3">{money(stats.total_current_worth)} today</span> : null}</h2></div>
          <Button asChild variant="ghost" className="rounded-full"><Link to="/inventory" data-testid="home-closet-all">Open closet <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
        {items === null ? <div className="grid grid-cols-4 md:grid-cols-8 gap-2.5">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}</div>
          : items.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Nothing here yet. <Link to="/add" className="underline underline-offset-4 text-foreground">Add your first pieces</Link>.</div>
          : <div className="grid grid-cols-4 md:grid-cols-8 gap-2.5" data-testid="home-closet-grid">
            {items.map((it) => <Link key={it.id} to="/inventory" className="group relative aspect-square rounded-xl bg-gray-50 overflow-hidden lift" title={it.name}><img src={itemImage(it)} alt={it.name || ""} loading="lazy" className="h-full w-full object-cover mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.04]" /></Link>)}
            <Link to="/add" className="aspect-square rounded-xl border border-dashed border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors" data-testid="home-closet-add-tile" aria-label="Add pieces"><Plus className="h-5 w-5" /></Link>
          </div>}
      </section>

      {/* feed */}
      <section>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div><p className="eyebrow">Fit pics</p><h2 className="font-display text-[26px] mt-1">From your people.</h2></div>
          <Button asChild variant="ghost" className="rounded-full"><Link to="/social" data-testid="home-feed-all">Open the feed <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
        {feed === null ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}</div>
          : feed.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="home-feed-empty">Nobody's shared a look yet. <Link to="/social?tab=people" className="underline underline-offset-4 text-foreground">Find people to follow</Link>.</div>
          : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-feed">{feed.map((p) => <PostCard key={p.id} post={p} onChange={() => api.feed("all").then((x) => setFeed(x.slice(0, 3))).catch(() => {})} />)}</div>}
      </section>

      {/* market */}
      <section>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div><p className="eyebrow"><span className="h-1.5 w-1.5 rounded-full bg-good animate-pulse" /> Live on ThreadList</p><h2 className="font-display text-[26px] mt-1">In your size right now.</h2></div>
          <Button asChild variant="ghost" className="rounded-full"><Link to="/market" data-testid="home-market-all">Browse <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
        {market === null ? <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div>
          : market.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Nothing in your size yet — add sizes to your pieces so we can match you.</div>
          : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="home-market">{market.map((it) => <MarketCard key={it.id} item={it} />)}</div>}
      </section>
    </div>
  );
}
